import React, { useState } from 'react';
import './DashboardHeader.css';

export interface DateRangeOption {
  label: string;
  days: number;
}

export const DATE_RANGE_OPTIONS: DateRangeOption[] = [
  { label: 'Today', days: 1 },
  { label: 'Last 7 Days', days: 7 },
  { label: 'Last 30 Days', days: 30 },
  { label: 'Last 90 Days', days: 90 },
  { label: 'Month to Date', days: new Date().getDate() },
  {
    label: 'Quarter to Date',
    days: (() => {
      const now = new Date();
      const qStartMonth = Math.floor(now.getMonth() / 3) * 3;
      const qStart = new Date(now.getFullYear(), qStartMonth, 1);
      return Math.max(1, Math.round((now.getTime() - qStart.getTime()) / 86400000));
    })(),
  },
];

interface Props {
  days: number;
  onDaysChange: (days: number) => void;
  onRefresh: () => void;
  lastUpdated: Date | null;
  refreshing?: boolean;
}

const timeAgo = (d: Date | null) => {
  if (!d) return 'never';
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
};

export const DashboardHeader: React.FC<Props> = ({ days, onDaysChange, onRefresh, lastUpdated, refreshing }) => {
  const [open, setOpen] = useState(false);
  const current = DATE_RANGE_OPTIONS.find(o => o.days === days) || DATE_RANGE_OPTIONS[2];

  return (
    <div className="dashh-header">
      <div>
        <h1 className="dashh-title">AssureX Overview</h1>
        <p className="dashh-subtitle">Monitor quality, testing performance, risks, and AI-driven productivity.</p>
      </div>
      <div className="dashh-actions">
        <span className="dashh-updated">Updated {timeAgo(lastUpdated)}</span>
        <button className="dashh-refresh-btn" onClick={onRefresh} title="Refresh" disabled={refreshing}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            className={refreshing ? 'dashh-spin' : ''}>
            <path d="M21 12a9 9 0 11-6.219-8.56"/><polyline points="21 3 21 9 15 9"/>
          </svg>
        </button>
        <div className="dashh-range-wrap" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}>
          <button className="dashh-range-btn" onClick={() => setOpen(v => !v)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            {current.label}
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
          </button>
          {open && (
            <div className="dashh-range-dropdown">
              {DATE_RANGE_OPTIONS.map(opt => (
                <button
                  key={opt.label}
                  className={`dashh-range-option${current.label === opt.label ? ' selected' : ''}`}
                  onClick={() => { onDaysChange(opt.days); setOpen(false); }}
                >
                  {current.label === opt.label && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardHeader;
